import { createContext, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import Swal from 'sweetalert2';
import { getDocument } from 'pdfjs-dist';
import { useShallow } from 'zustand/react/shallow';
import {
  Line,
  Rect,
  Group,
  IText,
  Canvas,
  Circle,
  Triangle,
  FabricImage,
  FabricObject,
  util,
  Object as FabricObject1,
  TPointerEventInfo,
  PencilBrush,
} from 'fabric';

import { debounce } from '@src/utils';
import { isImageURL } from '@src/helpers';

import { LocalStorage } from '@src/services';
import SocketService from '@src/services/SocketService';
import { ICanvasPayload } from '@src/types';

import useRoomContext from './useRoomContext';
import { usePeersStore, useRoomActionsStore, useWhiteboardSyncStore } from '@src/stores';
import { useTheme } from './ThemeContext';

// Whiteboard
const WB_GRID_SIZE = 20;
const WB_STROKE = '#cccccc63';
const Base64Prefix = 'data:application/pdf;base64,';

export const WhiteboardContext = createContext<any | undefined>(undefined);

interface IWhiteBoardContextProviderProps {
  children: ReactNode;
  isReadOnly?: boolean;
  canvasId?: string;
}

export const WhiteBoardContextProvider = ({
  children,
  isReadOnly = false,
  canvasId = 'wbCanvasV2',
}: IWhiteBoardContextProviderProps) => {
  const wbCanvasRef = useRef<Canvas | null>(null);
  const parentElement = useRef<HTMLDivElement | null>(null);

  const wbIsEraser = useRef(false);
  const wbIsRedoing = useRef(false);
  const wbGridVisible = useRef(false);
  const isRestoringCanvas = useRef(false);

  const lastWhiteboardInstance: any = useRef(null);
  const GridLineRef: any = useRef(null);

  const [wbPop, setWbPop] = useState<FabricObject[]>([]);
  const [isGridVisible, setIsGridVisible] = useState(false);
  const [wbIsDrawing, setWbIsDrawing] = useState(true);
  const [wbIsLock, setWbIsLock] = useState(true);
  const [wbIsTransparent, setWbIsTransparent] = useState(false);
  const [wbBackgroundColor, setWbBackgroundColor] = useState('#000e16');
  // Buffer for on-rejoin canvas data
  const [restoredCanvasObjects, setRestoredCanvasObjects] = useState<ICanvasPayload[]>([]);
  const lS = new LocalStorage();
  const localStorageSettings = lS.getLocalStorageSettings() || lS.SFU_SETTINGS;

  const { whiteboardSettings, setWhiteboardSettings } = useRoomActionsStore(
    useShallow((state) => ({
      whiteboardSettings: state.whiteboardSettings,
      setWhiteboardSettings: state.setWhiteboardSettings,
    }))
  );
  const isPipActive = useRoomActionsStore((state) => state.pipSettings.isActive);
  const { isHostUser } = usePeersStore(
    useShallow((state) => ({
      isHostUser: state.isHostUser,
    }))
  );
  const { currentPeer } = useRoomContext();
  const { theme } = useTheme();

  const InitWhiteboard = useCallback(() => {
    wbCanvasRef.current?.dispose();
    const newCanvas = new Canvas(canvasId);
    const brush = new PencilBrush(newCanvas);
    brush.color = '#FFFFFF';
    brush.width = 3;
    newCanvas.freeDrawingBrush = brush;
    newCanvas.isDrawingMode = !isReadOnly;
    newCanvas.hoverCursor = isReadOnly ? 'default' : 'crosshair';
    newCanvas.selection = !isReadOnly;
    newCanvas.skipTargetFind = isReadOnly;
    wbCanvasRef.current = newCanvas;

    // Replay any buffered canvas objects saved when the host/peer rejoined
    // `restoredCanvasObjects` contains the full batch of serialized Fabric object data
    // that arrived before the canvas was initialized. We call JsonToWbCanvas
    // for each payload to ‘enliven’ the JSON into Fabric objects and add them
    // back onto the canvas in the correct draw order.
    if (restoredCanvasObjects.length) {
      isRestoringCanvas.current = true;

      restoredCanvasObjects.forEach((json) => JsonToWbCanvas(json));
    }

    // Load initial canvas state: from local ref, or from sync store (for PiP opening mid-session)
    const jsonToLoad = lastWhiteboardInstance.current || useWhiteboardSyncStore.getState().getLastCanvasJson();
    if (jsonToLoad) {
      wbCanvasRef.current?.loadFromJSON(JSON.parse(jsonToLoad), () => {
        wbCanvasRef.current?.renderAll();
      });
    }

    if (!isReadOnly && !isHostUser) {
      if (wbIsLock) {
        disableCanvas(false);
      }
    }
    setupWhiteboard(isReadOnly);
    resizeCanvas();
  }, [isHostUser, lastWhiteboardInstance, wbIsLock, restoredCanvasObjects, isReadOnly, canvasId]);

  const resizeCanvas = useCallback(() => {
    if (!parentElement?.current) return undefined;

    // Read-only (PiP): skip ResizeObserver to avoid loop errors; resize once on mount only
    if (isReadOnly) {
      requestAnimationFrame(() => {
        setupWhiteboardCanvasSize();
        wbCanvasRef.current?.renderAll();
      });
      return undefined;
    }

    const element = parentElement.current;
    const observer = new ResizeObserver(() => {
      // Disconnect before mutating layout to prevent "ResizeObserver loop" error
      observer.disconnect();
      requestAnimationFrame(() => {
        try {
          setupWhiteboardCanvasSize();
          wbCanvasRef.current?.renderAll();
          if (wbGridVisible.current) {
            removeCanvasGrid();
            drawCanvasGrid();
          }
        } finally {
          if (element && element.isConnected) {
            observer.observe(element);
          }
        }
      });
    });

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [isReadOnly]);

  useEffect(() => {
    if (wbCanvasRef.current && wbCanvasRef.current.freeDrawingBrush) {
      const updatedBackgroundColor = wbIsTransparent ? wbBackgroundColor + 'b4' : wbBackgroundColor;
      wbCanvasRef.current.backgroundColor = updatedBackgroundColor;
      wbCanvasRef.current.renderAll();
    }
  }, [wbIsTransparent, wbBackgroundColor]);

  // Capture the full logical canvas area using an offscreen canvas (avoids modifying the live canvas)
  const captureCanvasDataUrl = useCallback(() => {
    if (!wbCanvasRef.current) return null;
    const canvas = wbCanvasRef.current;
    const jsonData = canvas.toJSON();

    // Derive logical dimensions from viewport transform
    // setupWhiteboardCanvasSize sets vpt = [containerW/1000, 0, 0, containerH/600, 0, 0]
    const vpt = canvas.viewportTransform;
    const logicalWidth = vpt && vpt[0] ? Math.ceil(canvas.getWidth() / vpt[0]) : canvas.getWidth();
    const logicalHeight = vpt && vpt[3] ? Math.ceil(canvas.getHeight() / vpt[3]) : canvas.getHeight();

    // Draw onto an offscreen canvas at the logical dimensions
    const offscreen = document.createElement('canvas');
    offscreen.width = logicalWidth;
    offscreen.height = logicalHeight;
    const ctx = offscreen.getContext('2d');
    if (!ctx) return null;

    // Fill background
    const bg = jsonData.background || canvas.backgroundColor;
    if (bg) {
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, logicalWidth, logicalHeight);
    }

    // Draw the main canvas image scaled to fill the logical dimensions
    try {
      const lowerCanvas = canvas.getElement();
      ctx.drawImage(lowerCanvas, 0, 0, logicalWidth, logicalHeight);
    } catch {
      return null;
    }

    return offscreen.toDataURL('image/png');
  }, []);

  // Debounced function to sync canvas image (data URL) to store for PiP display
  const debouncedSyncImage = useMemo(() => {
    return debounce(() => {
      if (!wbCanvasRef.current || isReadOnly) return;
      try {
        const dataUrl = captureCanvasDataUrl();
        if (dataUrl) useWhiteboardSyncStore.getState().setLastCanvasDataUrl(dataUrl);
      } catch {
        // Canvas might be disposed or not ready
      }
    }, 150);
  }, [isReadOnly, captureCanvasDataUrl]);

  // When PiP opens, push current canvas state to store so PiP can load it
  useEffect(() => {
    if (isPipActive && !isReadOnly && wbCanvasRef.current) {
      const json = JSON.stringify(wbCanvasRef.current.toJSON());
      useWhiteboardSyncStore.getState().setLastCanvasJson(json);
      // Also sync the image immediately (no debounce) for PiP initial display
      try {
        const dataUrl = captureCanvasDataUrl();
        if (dataUrl) useWhiteboardSyncStore.getState().setLastCanvasDataUrl(dataUrl);
      } catch {
        // Ignore
      }
    }
  }, [isPipActive, isReadOnly, captureCanvasDataUrl]);

  const whiteboardIsDrawingMode = useCallback(
    (status: boolean) => {
      if (!wbCanvasRef.current) {
        return;
      }
      if (!isHostUser) {
        if (wbIsLock) {
          return;
        }
      }

      setWbIsDrawing(status);
      wbCanvasRef.current.isDrawingMode = status;
      wbCanvasRef.current.selection = !status;
      wbCanvasRef.current.skipTargetFind = status;

      if (status) {
        wbIsEraser.current = false;
      } else {
        wbCanvasRef.current.hoverCursor = 'default';
      }
    },
    [wbIsLock, isHostUser]
  );

  const setupWhiteboardCanvas = useCallback(() => {
    whiteboardIsDrawingMode(true);
  }, [whiteboardIsDrawingMode]);

  const currentPeerRef = useRef(currentPeer);
  useEffect(() => {
    currentPeerRef.current = currentPeer;
  }, [currentPeer]);

  const sendCanvasUpdate = useMemo(() => {
    return debounce((serialized) => {
      if (isApplyingRemoteUpdate.current || isRestoringCanvas.current) {
        isRestoringCanvas.current = false;

        return;
      }
      SocketService.send('wbCanvasToJson', {
        canvasData: serialized,
        peerInfo: {
          peerName: currentPeerRef.current?.peer_info.peer_name,
          peerId: SocketService.getSocketId(),
        },
      });
    }, 100);
  }, []); // still only created once

  const wbCanvasToJson = useCallback(
    (e?: any) => {
      const object = e?.target ?? e;
      if (!object || !wbCanvasRef.current || isReadOnly) return;

      const serialized = object.toObject();
      const canvasJson = JSON.stringify(wbCanvasRef.current.toJSON());
      lastWhiteboardInstance.current = canvasJson;

      // Sync to store for PiP to load when opening mid-session
      useWhiteboardSyncStore.getState().setLastCanvasJson(canvasJson);
      debouncedSyncImage();

      sendCanvasUpdate(serialized);

      wbCanvasRef.current?.renderAll();
    },
    [isReadOnly]
  );

  const mouseDown = useCallback((e: TPointerEventInfo) => {
    if (wbCanvasRef.current && wbIsEraser.current && e.target) {
      wbCanvasRef.current.remove(e.target);
      return;
    }
  }, []);

  const mouseMove = useCallback(() => {
    if (wbCanvasRef.current) {
      if (wbIsEraser.current) {
        wbCanvasRef.current.hoverCursor = 'not-allowed';
        return;
      } else {
        wbCanvasRef.current.hoverCursor = 'move';
      }
      if (!wbIsDrawing) return;
    }
  }, [wbIsDrawing]);

  const objectAdded = useCallback(() => {
    if (!wbIsRedoing) setWbPop([]);
    wbIsRedoing.current = false;
  }, [wbIsRedoing, isHostUser]);

  const setupWhiteboardLocalListeners = useCallback(() => {
    if (wbCanvasRef.current) {
      wbCanvasRef.current.on('mouse:down', function (e) {
        mouseDown(e);
      });
      wbCanvasRef.current.on('mouse:move', function () {
        mouseMove();
      });
      wbCanvasRef.current.on('object:added', function (e) {
        if (isApplyingRemoteUpdate.current) {
          return; // 🚫 Skip triggering wbCanvasToJson
        }

        objectAdded();
        wbCanvasToJson(e);
      });
    }
  }, [mouseDown, mouseMove, objectAdded, wbCanvasToJson]);

  const setWhiteboardSize = useCallback((w: number, h: number) => {
    if (wbCanvasRef.current) {
      wbCanvasRef.current.width = Math.ceil(w);
      wbCanvasRef.current.height = Math.ceil(h);
    }
  }, []);

  const setupWhiteboardCanvasSize = useCallback(() => {
    if (wbCanvasRef.current && parentElement?.current) {
      const rect = parentElement.current.getBoundingClientRect();
      const widthOffset = isReadOnly ? 0 : 50;
      const heightOffset = isReadOnly ? 0 : 20;
      const containerWidth = rect.width - widthOffset;
      const containerHeight = rect.height - heightOffset;
      const zoomFactorWidth = containerWidth / 1000;
      const zoomFactorHeight = containerHeight / 600;

      wbCanvasRef.current.setDimensions({ width: containerWidth, height: containerHeight });
      wbCanvasRef.current.setViewportTransform([zoomFactorWidth, 0, 0, zoomFactorHeight, 0, 0]);
    }
  }, [isReadOnly]);

  const setupWhiteboard = useCallback(
    (readOnly = false) => {
      if (!readOnly) {
        setupWhiteboardCanvas();
      } else {
        // Read-only: ensure drawing and selection are disabled
        if (wbCanvasRef.current) {
          wbCanvasRef.current.isDrawingMode = false;
          wbCanvasRef.current.selection = false;
          wbCanvasRef.current.skipTargetFind = true;
        }
      }
      setupWhiteboardCanvasSize();
      if (!readOnly) {
        setupWhiteboardLocalListeners();
      }
    },
    [setupWhiteboardCanvas, setupWhiteboardCanvasSize, setupWhiteboardLocalListeners]
  );

  const removeCanvasGrid = useCallback(() => {
    if (!wbCanvasRef.current) {
      return;
    }
    wbCanvasRef.current.remove(GridLineRef.current);
    wbCanvasRef.current.renderAll();
    GridLineRef.current = null;
  }, []);

  const createGridLine = useCallback((x1: number, y1: number, x2: number, y2: number) => {
    return new Line([x1, y1, x2, y2], {
      stroke: WB_STROKE,
      selectable: false,
      evented: false,
    });
  }, []);

  const drawCanvasGrid = useCallback(() => {
    if (!wbCanvasRef.current) {
      return;
    }
    const width = wbCanvasRef.current.getWidth();
    const height = wbCanvasRef.current.getHeight();
    let lines = [];

    let gapX = WB_GRID_SIZE;
    let gapY = WB_GRID_SIZE;

    // Draw vertical lines
    for (let i = 0; i <= 100; i++) {
      lines.push(createGridLine(i + gapY, 0, i + gapY, height + 500));
      gapY = gapY + 20;
    }

    // Draw horizontal lines
    for (let i = 0; i <= 100; i++) {
      lines.push(createGridLine(0, i + gapX, width + 500, i + gapX));
      gapX = gapX + 20;
    }

    // Create a group for grid lines and send it to the back
    const gridGroup = new Group(lines, { selectable: false, evented: false });
    GridLineRef.current = gridGroup;
    wbCanvasRef.current.add(gridGroup);
    wbCanvasRef.current.sendObjectToBack(gridGroup);
    wbCanvasRef.current.renderAll();
  }, [createGridLine]);

  const toggleCanvasGrid = useCallback(() => {
    wbGridVisible.current = !wbGridVisible.current;
    setIsGridVisible(wbGridVisible.current);
    wbGridVisible.current ? drawCanvasGrid() : removeCanvasGrid();
  }, [drawCanvasGrid, removeCanvasGrid]);

  const wbDrawing = useCallback((status: boolean) => {
    if (!wbCanvasRef.current) {
      return;
    }
    wbCanvasRef.current.isDrawingMode = status; // Disable free drawing
    wbCanvasRef.current.selection = status; // Disable object selection
    wbCanvasRef.current.forEachObject(function (obj) {
      obj.selectable = status; // Make all objects unselectable
    });
  }, []);

  const isApplyingRemoteUpdate = useRef(false);

  const handleSaveJsonFromSocket = useCallback((items: ICanvasPayload[]) => {
    setRestoredCanvasObjects(items);
  }, []);

  const JsonToWbCanvas = useCallback(async (json: any) => {
    if (!wbCanvasRef.current || !json?.canvasData) return;
    try {
      const parsed = json.canvasData;

      // 🛑 Set the flag before modifying the canvas
      isApplyingRemoteUpdate.current = true;

      const [fabricObj]: FabricObject1[] = await util.enlivenObjects([parsed]);
      wbCanvasRef.current.add(fabricObj);
      wbCanvasRef.current.renderAll();
      setupWhiteboardCanvasSize();

      // Sync to store so PiP gets latest state when opening mid-session (for both host and peers)
      const canvasJson = JSON.stringify(wbCanvasRef.current.toJSON());
      useWhiteboardSyncStore.getState().setLastCanvasJson(canvasJson);
      debouncedSyncImage();
    } catch (e) {
      console.error('Error parsing or adding object to canvas:', e);
    } finally {
      // ✅ Always reset the flag afterward
      isApplyingRemoteUpdate.current = false;
    }
  }, []);

  const wbCanvasRedo = useCallback(() => {
    if (wbCanvasRef.current && wbPop.length > 0) {
      wbCanvasRef.current.discardActiveObject();
      wbIsRedoing.current = true;
      const lastItem = wbPop.pop();
      lastItem && wbCanvasRef.current.add(lastItem);
    }
  }, [wbPop]);

  const wbCanvasUndo = useCallback(() => {
    if (wbCanvasRef.current && wbCanvasRef.current._objects.length > 0) {
      wbCanvasRef.current.discardActiveObject();
      const lastItem = wbCanvasRef.current._objects.pop();
      lastItem && wbPop.push(lastItem);
      wbCanvasRef.current.renderAll();
    }
  }, [wbPop]);

  const whiteboardAction = useCallback(
    (data: any, emit = true) => {
      if (emit) {
        SocketService.send('whiteboardAction', data);
      }

      switch (data.action) {
        case 'undo':
          wbCanvasUndo();
          if (wbCanvasRef.current) {
            useWhiteboardSyncStore.getState().setLastCanvasJson(JSON.stringify(wbCanvasRef.current.toJSON()));
            debouncedSyncImage();
          }
          break;
        case 'redo':
          wbCanvasRedo();
          if (wbCanvasRef.current) {
            useWhiteboardSyncStore.getState().setLastCanvasJson(JSON.stringify(wbCanvasRef.current.toJSON()));
            debouncedSyncImage();
          }
          break;
        case 'clear':
          wbCanvasRef.current?.clear();
          GridLineRef.current = null;
          wbGridVisible.current = false;
          setIsGridVisible(false);
          useWhiteboardSyncStore
            .getState()
            .setLastCanvasJson(wbCanvasRef.current ? JSON.stringify(wbCanvasRef.current.toJSON()) : null);
          debouncedSyncImage();
          break;
        case 'transparent':
          toogleTransparent();
          break;
        case 'background':
          setWbBackgroundColor(data.color);
          break;
        case 'grid':
          toggleCanvasGrid();
          break;
        case 'lock':
          setWbIsLock(true);
          if (!isHostUser) {
            disableCanvas(false);
          }
          break;
        case 'unlock':
          setWbIsLock(false);
          if (!isHostUser) {
            disableCanvas(true);
          }
          break;

        case 'openWhiteboard':
          setWhiteboardSettings({
            ...whiteboardSettings,
            isActive: true,
          });
          break;

        case 'closeWhiteboard':
          removeCanvasGrid();
          if (wbCanvasRef.current) {
            const wbCanvasJson = JSON.stringify(wbCanvasRef.current.toJSON());
            lastWhiteboardInstance.current = wbCanvasJson;
            useWhiteboardSyncStore.getState().setLastCanvasJson(wbCanvasJson);
          }
          // Clear the PiP image when whiteboard closes
          useWhiteboardSyncStore.getState().setLastCanvasDataUrl(null);
          debouncedSyncImage.cancel?.();
          setWhiteboardSettings({
            ...whiteboardSettings,
            isActive: false,
          });
          break;
        default:
          break;
      }
    },
    [currentPeer, wbIsLock, wbCanvasRedo, wbCanvasUndo, wbDrawing, setWhiteboardSettings]
  );

  const getWhiteboardAction = useCallback((action: string) => {
    return {
      peer_name: '',
      action,
    };
  }, []);

  const wbUpdate = useCallback(
    (isRulesActive: boolean) => {
      if (!isRulesActive) {
        wbCanvasToJson();
        whiteboardAction(getWhiteboardAction(wbIsLock ? 'lock' : 'unlock'));
      }
    },
    [getWhiteboardAction, wbCanvasToJson, wbIsLock, , whiteboardAction]
  );

  const whiteboardIsEraser = useCallback(
    (status: boolean) => {
      whiteboardIsDrawingMode(!status);
      wbIsEraser.current = status;
    },
    [whiteboardIsDrawingMode]
  );

  const addWbCanvasObj = useCallback(
    (obj: FabricObject) => {
      if (obj) {
        wbCanvasRef.current?.add(obj);
        wbCanvasRef.current?.setActiveObject(obj);
        whiteboardIsDrawingMode(false);
        wbIsEraser.current = false;
        wbCanvasToJson();
      } else {
        console.error('Invalid input. Expected an obj of canvas elements');
      }
    },
    [wbCanvasToJson, whiteboardIsDrawingMode]
  );

  const renderImageToCanvas = useCallback(
    (wbCanvasImg: any) => {
      if (wbCanvasImg && wbCanvasImg.size > 0) {
        const reader = new FileReader();
        reader.onload = (event: any) => {
          const imgObj = new Image();
          imgObj.src = event.target.result;
          imgObj.onload = function () {
            const image = new FabricImage(imgObj);
            image.set({ top: 0, left: 0 }).scale(0.3);
            addWbCanvasObj(image);
          };
        };
        reader.readAsDataURL(wbCanvasImg);
      }
    },
    [addWbCanvasObj]
  );

  const readBlob = useCallback((blob: Blob) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.addEventListener('load', () => resolve(reader.result));
      reader.addEventListener('error', reject);
      reader.readAsDataURL(blob);
    });
  }, []);

  const loadPDF = useCallback(
    async (pdfData: any, pages?: any) => {
      pdfData = pdfData instanceof Blob ? await readBlob(pdfData) : pdfData;
      const data = atob(pdfData.startsWith(Base64Prefix) ? pdfData.substring(Base64Prefix.length) : pdfData);
      try {
        const pdf = await getDocument({ data }).promise;
        const numPages = pdf.numPages;
        const canvases = await Promise.all(
          Array.from({ length: numPages }, (_, i) => {
            const pageNumber = i + 1;
            if (pages && pages.indexOf(pageNumber) === -1) return null;
            return pdf.getPage(pageNumber).then(async (page: any) => {
              const viewport = page.getViewport({ scale: window.devicePixelRatio });
              const canvas = document.createElement('canvas');
              const context = canvas.getContext('2d');
              canvas.height = viewport.height;
              canvas.width = viewport.width;
              const renderContext = {
                canvasContext: context,
                viewport: viewport,
              };
              await page.render(renderContext).promise;
              return canvas;
            });
          })
        );
        return canvases.filter((canvas) => canvas !== null);
      } catch (error: any) {
        console.error('Error loading PDF', error.message);
        throw error.message;
      }
    },
    [readBlob]
  );

  const pdfToImage = useCallback(
    async (pdfData: any, canvas: Canvas) => {
      const scale = 1 / window.devicePixelRatio;
      try {
        const canvases = await loadPDF(pdfData);
        canvases.forEach(async (c) => {
          // canvas.add(
          //   c && new FabricImage(c, {
          //     scaleX: scale,
          //     scaleY: scale,
          //   })
          // );
        });
      } catch (error: any) {
        console.error('Error converting PDF to images', error.message);
        throw error.message;
      }
    },
    [loadPDF]
  );

  const renderPdfToCanvas = useCallback(
    async (wbCanvasPdf: any) => {
      if (wbCanvasPdf && wbCanvasPdf.size > 0) {
        const reader = new FileReader();
        reader.onload = async function (event) {
          wbCanvasRef.current?.requestRenderAll();
          wbCanvasRef.current && (await pdfToImage(event?.target?.result, wbCanvasRef.current));
          whiteboardIsDrawingMode(false);
          wbCanvasToJson();
        };
        reader.readAsDataURL(wbCanvasPdf);
      }
    },
    [pdfToImage, wbCanvasToJson, whiteboardIsDrawingMode]
  );

  const whiteboardAddObj = useCallback(
    (type: string) => {
      if (!isHostUser) {
        if (wbIsLock) {
          return;
        }
      }
      switch (type) {
        case 'imgUrl':
          Swal.fire({
            background: localStorageSettings.swalBackground,
            title: 'Image URL',
            input: 'text',
            showCancelButton: true,
            confirmButtonText: 'OK',
            showClass: { popup: 'animate__animated animate__fadeInDown' },
            hideClass: { popup: 'animate__animated animate__fadeOutUp' },
          }).then((result) => {
            if (result.isConfirmed) {
              const wbCanvasImgURL: string = result.value;
              if (isImageURL(wbCanvasImgURL)) {
                FabricImage.fromURL(wbCanvasImgURL, {}, (myImg: FabricImage) => {
                  addWbCanvasObj(myImg);
                });
              }
            }
          });
          break;

        case 'text':
          addWbCanvasObj(
            new IText('Lorem Ipsum', {
              top: 0,
              left: 0,
              fill: wbCanvasRef.current?.freeDrawingBrush?.color,
            })
          );
          break;
        case 'line':
          addWbCanvasObj(
            new Line([50, 100, 200, 200], {
              top: 0,
              left: 0,
              fill: wbCanvasRef.current?.freeDrawingBrush?.color,
              strokeWidth: wbCanvasRef.current?.freeDrawingBrush?.width,
              stroke: wbCanvasRef.current?.freeDrawingBrush?.color,
            })
          );
          break;
        case 'circle':
          addWbCanvasObj(
            new Circle({
              radius: 50,
              fill: 'transparent',
              stroke: wbCanvasRef.current?.freeDrawingBrush?.color,
              strokeWidth: wbCanvasRef.current?.freeDrawingBrush?.width,
            })
          );
          break;
        case 'rect':
          addWbCanvasObj(
            new Rect({
              top: 0,
              left: 0,
              width: 150,
              height: 100,
              fill: 'transparent',
              stroke: wbCanvasRef.current?.freeDrawingBrush?.color,
              strokeWidth: wbCanvasRef.current?.freeDrawingBrush?.width,
            })
          );
          break;
        case 'triangle':
          addWbCanvasObj(
            new Triangle({
              top: 0,
              left: 0,
              width: 150,
              height: 100,
              fill: 'transparent',
              stroke: wbCanvasRef.current?.freeDrawingBrush?.color,
              strokeWidth: wbCanvasRef.current?.freeDrawingBrush?.width,
            })
          );
          break;
        default:
          break;
      }
    },
    [addWbCanvasObj, localStorageSettings.swalBackground, renderImageToCanvas, renderPdfToCanvas]
  );

  const toogleTransparent = useCallback(() => {
    setWbIsTransparent((prev) => {
      return !prev;
    });
  }, []);

  const changeBrushColor = useCallback(
    (color: string) => {
      if (!isHostUser) {
        if (wbIsLock) {
          return;
        }
      }
      if (wbCanvasRef.current && wbCanvasRef.current.freeDrawingBrush) {
        wbCanvasRef.current.freeDrawingBrush.color = color;
      }
    },
    [isHostUser, wbIsLock]
  );

  const changeCanvasBackgroundColor = useCallback(
    (color: string, emmit: boolean) => {
      if (!isHostUser) {
        if (wbIsLock) {
          return;
        }
      }
      whiteboardAction({ action: 'background', color: color }, emmit);
    },
    [isHostUser, wbIsLock]
  );

  const debouncedBrushColor = useMemo(() => debounce(changeBrushColor, 200), [changeBrushColor]);

  const debouncedchangeCanvasBackgroundColor = useMemo(
    () => debounce(changeCanvasBackgroundColor, 200),
    [changeCanvasBackgroundColor]
  );

  const handleCloseWhiteboard = (emit: boolean) => {
    if (!isHostUser) {
      return;
    }
    whiteboardAction({ action: 'closeWhiteboard' }, emit);
  };

  const handleOpenWhiteboard = (emit: boolean) => {
    if (!isHostUser) {
      return;
    }
    whiteboardAction({ action: 'openWhiteboard' }, emit);
  };

  const disableCanvas = useCallback((status: any) => {
    if (wbCanvasRef.current) {
      wbCanvasRef.current.isDrawingMode = status;
      wbCanvasRef.current.selection = status;
      wbCanvasRef.current.skipTargetFind = !status;
    }
  }, []);

  const wbCanvasSaveImg = useCallback(() => {
    if (wbCanvasRef.current) {
      if (
        !wbCanvasRef.current._objects.length ||
        (isGridVisible && wbCanvasRef.current._objects.length === 1) ||
        !usePeersStore.getState().isHostUser
      ) {
        return;
      }

      const dataURL = wbCanvasRef.current.toDataURL({
        format: 'png',
        multiplier: 1,
        width: wbCanvasRef.current.getWidth(),
        height: wbCanvasRef.current.getHeight() * 1.4,
      });
      const link = document.createElement('a');
      link.href = dataURL;
      const formattedDate = new Date().toLocaleDateString('en-US', {
        month: 'numeric',
        day: 'numeric',
        year: 'numeric',
      });
      link.download = `whiteboard-${formattedDate}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }, [isGridVisible]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Cancel pending debounced function calls
      sendCanvasUpdate.cancel?.();
      debouncedBrushColor.cancel?.();
      debouncedchangeCanvasBackgroundColor.cancel?.();
      debouncedSyncImage.cancel?.();

      // Dispose canvas to free Fabric.js resources
      if (wbCanvasRef.current) {
        wbCanvasRef.current.dispose();
        wbCanvasRef.current = null;
      }
    };
  }, [sendCanvasUpdate, debouncedBrushColor, debouncedchangeCanvasBackgroundColor, debouncedSyncImage]);

  return (
    <WhiteboardContext.Provider
      value={{
        setupWhiteboardLocalListeners,
        setupWhiteboardCanvasSize,
        setupWhiteboardCanvas,
        setWhiteboardSize,
        wbUpdate,
        whiteboardIsDrawingMode,
        getWhiteboardAction,
        whiteboardIsEraser,
        whiteboardAddObj,
        whiteboardAction,
        toggleCanvasGrid,
        removeCanvasGrid,
        setupWhiteboard,
        drawCanvasGrid,
        JsonToWbCanvas,
        handleSaveJsonFromSocket,
        wbCanvasToJson,
        objectAdded,
        wbDrawing,
        mouseMove,
        mouseDown,
        toogleTransparent,
        debouncedBrushColor,
        debouncedchangeCanvasBackgroundColor,
        handleCloseWhiteboard,
        handleOpenWhiteboard,
        InitWhiteboard,
        resizeCanvas,
        wbCanvasSaveImg,
        wbCanvasRef,
        wbIsTransparent,
        wbBackgroundColor,
        isGridVisible,
        wbIsDrawing,
        whiteboardSettings,
        parentElement,
        wbIsLock,
        isHostUser,
        theme,
        canvasId,
      }}
    >
      {children}
    </WhiteboardContext.Provider>
  );
};
